import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Box, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { selectLocationLoading } from "../../slices/location";
import FormikControl from "../../components/common/FormikControl";
import { addTemplate, duplicateTemplate } from "../../slices/formTemplate";
import { notify } from "../../utils/methods/notification";
import { useNavigateTo } from "../../../lib/form-builder/hooks/useNavigate";

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .min(1, " Name must be at least 1 character long")
        .max(50, " Name cannot exceed 50 characters")
        .required(" Name is required"),
    description: Yup.string()
        .min(1, "Description must be at least 1 character long")
        .max(255, "Description cannot exceed 255 characters")
        .required("Description is required"),
    duration: Yup.number().nullable().min(1, "Duration must be at least 1").max(100, "Duration cannot be more than 100").required("Duration is required"),
    select_version: Yup.string().when("template_type", {
        is: "2",
        then: Yup.string().required("Version is required"),
    }),
    select_template: Yup.string().when("template_type", {
        is: "2",
        then: Yup.string().required("Template is required"),
    }),
});

 const initialValues = {
        template_type: 1,
        name: "",
        description: "",
        duration:"",
        select_template:"",
        select_version:"",
    };

const AddTemplateForm = ({tailoredDuplicate}) => {
    const dispatch=useDispatch()
    const navigateTo = useNavigateTo();
    const isLoading = useSelector(selectLocationLoading)
    const [versionsOptions, setVersionsOptions]=useState([])
    const [isSubmitting, setIsSubmitting]=useState(false)
    const {templateDropdownlist} = useSelector((state) => state.bmrFbl);
    const tempalteOptions = templateDropdownlist?.map((items) => ({
        value: items?.id, 
        label :items?.name
    }))?.sort((a, b) => a.label?.localeCompare(b.label));
    const handleSubmit = async (values) => {
        try {
            setIsSubmitting(true)
            if (!templateType) {
                const payload = {
                    name: values.name,
                    duration: values.duration,
                    description: values.description,
                }
                const res = await dispatch(addTemplate(payload)).unwrap()
                notify.success(res.message);
                navigateTo(`/bmr/fb/${res?.data?.id}/permission`)
            } else {
                const payload = {
                    name: values.name,
                    version: tailoredDuplicate ? tailoredDuplicate.versions?.[0]?.value : values.select_version,
                    duration: values.duration,
                    description: values.description,
                }
                const response = await dispatch(duplicateTemplate({ id: values.select_template, payload })).unwrap()
                notify.success(response.message);
                navigateTo(`/bmr/fb/${response?.data?.newTemplate?.id}/permission`)
            }

        } catch (error) {
            notify.error(error?.message || "Something went wrong")
        }finally{
            setIsSubmitting(false)
        }
    }
    
      const formik = useFormik({
        // initialValues: tailoredDuplicate ? {...initialValues, template_type: 2} : initialValues,
        initialValues: tailoredDuplicate
  ? {
      ...initialValues,
      template_type: 2,
      select_template: tailoredDuplicate?.select_template?.[0]?.value ?? "",
      select_version: tailoredDuplicate?.versions?.[0]?.value ?? "",
    }
  : initialValues,
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true
    })
    let templateType= formik.values?.template_type == 2

    const typeOptions = [
        { value: 1, label: "New template" },
        { value: 2, label: "Clone template " }
    ]
    const formOptions = [
       ...(!tailoredDuplicate ? [{
            label: 'Select template type',
            type: 'radio',
            name: 'template_type',
            options: typeOptions,
            placeholder: 'Select Template',
        }] : []),

        {
            label: 'Name',
            type: 'text',
            name: 'name',
            required: true,
            placeholder: "Enter name",
        },
        ...(templateType
            ? [
                {
                    label: 'Select template',
                    type: 'select',
                    name: 'select_template',
                    placeholder: 'Select template',
                    options:tailoredDuplicate ? tailoredDuplicate.select_template:tempalteOptions,
                    required: true,
                    disabled:tailoredDuplicate?true:false,
                },
                {
                    label: 'Select Version',
                    type: 'select',
                    name: 'select_version',
                    options:tailoredDuplicate ?tailoredDuplicate.versions:versionsOptions,
                    placeholder: 'Select Version',
                    required: true,
                    // disabled:true,
                },
            ]
            : []),
        {
            label: 'Duration in Day(s)',
            type: 'number',
            name: 'duration',
            required: true,
            placeholder: "Enter Duration",
        },
        {
            label: 'Description',
            type: 'textarea',
            name: 'description',
            required: true,
            placeholder: 'Enter description'
        },
    ];

    const versionsOption = templateDropdownlist.find((item) => item.id === formik.values.select_template)
    useEffect(() => {
        if (versionsOption) {
            const versionList = versionsOption.versions.map((ver) => ({
                value: ver.version,
                label: `V.${ver.version}`
            }))
            setVersionsOptions(versionList)
        }
    }, [formik.values.select_template])

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} className="mb-3">
                    {formOptions.map((props, key) => (
                        <Grid key={key} item xs={12}>
                            <FormikControl formik={formik} {...props} />
                        </Grid>
                    ))}
                </Grid>
                <Grid container spacing={2} className="mt-3">
                    <Grid item xs={12}>
                        <LoadingButton loading={isLoading}  disabled={isSubmitting} fullWidth type="submit" variant="contained">{(templateType ? ' Duplicate' : ' Submit')}
                            </LoadingButton>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
}

export default AddTemplateForm;